// src/pages/HomePage.tsx
import React, { useState, useEffect } from 'react'; // Import useState and useEffect
import { useNavigate } from 'react-router-dom';
// import { motion } from 'framer-motion';
import { Flame, Wind, CheckCircle2, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { ProductCard } from '../components/ProductCard';
// import { cn } from '../lib/utils';
import { Product } from '../types/Product'; // Import Product interface from new location

interface HomePageProps {
  // products prop removed
}

const HomePage: React.FC<HomePageProps> = () => { // Removed products from props
  const navigate = useNavigate();
  const [featuredProducts, setFeaturedProducts] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const BACKEND_URL_ROOT = process.env.REACT_APP_BACKEND_URL_ROOT || 'http://localhost:3001';
  const API_PRODUCTS_URL = `${BACKEND_URL_ROOT}/api/products`;

  useEffect(() => {
    const fetchFeaturedProducts = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await fetch(API_PRODUCTS_URL); // Use API_PRODUCTS_URL
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ message: 'Failed to fetch products and parse error response.' }));
          throw new Error(errorData.message || `Failed to fetch products. Status: ${response.status}`);
        }
        const data: Product[] = await response.json();
        setFeaturedProducts(data.slice(0, 3)); // Take the first 3 for featured
      } catch (err: any) {
        console.error("Fetch featured products error:", err);
        setError(err.message || 'An unexpected error occurred while fetching featured products.');
        setFeaturedProducts([]);
      } finally {
        setIsLoading(false);
      }
    };
    fetchFeaturedProducts();
  }, [API_PRODUCTS_URL]); // Add API_PRODUCTS_URL here

  return (
    <div className="home-page-container">
      <section className="hero-section">
        <h1 className="hero-title">
          <Flame className="hero-icon-left" />
          Your Trusted HVAC Source
          <Wind className="hero-icon-right" />
        </h1>
        <p className="hero-description">
          Empowering homes and businesses with top-quality HVAC equipment and expertise. We are committed to providing reliable, energy-efficient solutions from leading manufacturers, ensuring optimal comfort and performance.
        </p>
        <div className="hero-buttons">
          <Button 
            variant="default" 
            size="lg" 
            className="hero-button-primary"
            onClick={() => navigate('/products')} // Use navigate
          >
            <CheckCircle2 className="icon" />
            Explore Our Catalog
          </Button>
          <Button 
            variant="outline" 
            size="lg" 
            className="hero-button-secondary"
            onClick={() => navigate('/contact')} // Use navigate
          >
            Request a Quote
          </Button>
        </div>
      </section>

      <section className="featured-products-section">
        <h2 className="section-title">Featured Products</h2>
        {isLoading && <p>Loading featured products...</p>}
        {error && <p style={{ color: 'red' }}>Error: {error}</p>}
        {!isLoading && !error && featuredProducts.length === 0 && <p>No featured products available at the moment.</p>}
        {!isLoading && !error && featuredProducts.length > 0 && (
          <div className="product-grid">
            {featuredProducts.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        )}
        <div className="view-all-link-container">
          <button 
            onClick={() => navigate('/products')}
            className="view-all-link"
          >
            View All Products <ChevronRight className="icon-inline" />
          </button>
        </div>
      </section>

      <section className="testimonials-section">
        <h2 className="section-title">Customer Testimonials</h2>
        <div className="testimonials-grid">
          <div className="testimonial-item">
            <p className="testimonial-text">"Great products and excellent service!"</p>
            <p className="testimonial-author">- John Smith, Homeowner</p>
          </div>
          <div className="testimonial-item">
            <p className="testimonial-text">"We rely on Best HVAC for all our equipment needs."</p>
            <p className="testimonial-author">- ACME Contracting</p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
